//React
import React from "react";

//Helpers
import graphics from "../helpers/graphics";
import validator from "../helpers/validator";

//Objects
import SceneItem from "../interactableObjects/sceneObject";
import InteractableObject from "../interactableObjects/interactableObject";
import ControllableObject from "../interactableObjects/controllableObject";
import CustomObject from "../interactableObjects/customObject";
import CompositeInteractableObject from "../interactableObjects/compositeInteractableObject";

//Types
import { allObjectProps } from "../helpers/validator";
import { objectPropsResult } from "../helpers/validator";
import { objectType } from "../helpers/validator";
import { Maybe, IOData } from "../types";
import {
  CustomObjectProps,
  IOProps,
  SceneItemProps,
} from "../types/SceneTypes";
import { CompositeIOProps } from "../interactableObjects/compositeInteractableObject";

class IOFactory {
  private static instance: IOFactory;

  private constructor() {
    //Intentionally blank
  }

  public static getInstance() {
    if (!this.instance) this.instance = new IOFactory();
    return this.instance;
  }

  public createObject(
    {
      id,
      name,
      type,
      image: imageURL,
      altText,
      position,
      modal,
      onDropItems,
      onclick,
      states,
      defaultState,
      text,
      defaultUserInput,
      currentUserInput,
      label,
      modals,
      buttons,
      customComponentID,
      unscalable,
      objectScale,
      moveMenu,
      objects,
    }: IOData,
    userScale: number,
    sceneScale: number,
    stateKeys: string[] = [],
    stateValues: string[] = [],
    controlsOptions: string[] = []
  ): any {
    const allProps: allObjectProps = {
      id: id,
      name: name,
      type: type,
      imageURL: imageURL,
      imageAltText: altText || "",
      position: position,
      stateKeys: stateKeys,
      stateValues: stateValues,
      controlsOptions: controlsOptions,
      currentUserInput: currentUserInput,
    };

    const validationResult: objectPropsResult = validator
      .getInstance()
      .validateObjectProps(allProps);

    const image = graphics
      .getInstance()
      .loadImage(
        validationResult.image,
        validationResult.alt,
        validationResult.text_subs,
        userScale,
        0.766,
        objectScale
      );

    // eslint-disable-next-line
    let obj;

    if (validationResult.type == objectType.invalid) return <></>;
    else if (validationResult.type == objectType.sceneObject) {
      const props: SceneItemProps = {
        id: validationResult.id,
        name: validationResult.name,
        alt: validationResult.alt,
        image: image,
        position: validationResult.position,
        text: text,
        unscalable,
        userScale,
        sceneScale,
        objectScale,
      };
      return <SceneItem ref={(ref) => (obj = ref)} {...props} />;
    } else if (validationResult.type == objectType.interactableObject) {
      const props: IOProps = {
        id: validationResult.id,
        states: states ? states : [],
        name: validationResult.name,
        alt: validationResult.alt,
        position: validationResult.position,
        image: image,
        modal: modal ? modal : "",
        onDropItems: onDropItems ? onDropItems : [],
        onclick: onclick ? onclick : [],
        defaultState: defaultState ? defaultState : "",
        defaultUserInput: defaultUserInput ? defaultUserInput : "",
        userScale,
        sceneScale,
        objectScale,
        text: text,
        label,
        modals,
        buttons,
      };
      return <InteractableObject ref={(ref) => (obj = ref)} {...props} />;
    } else if (validationResult.type == objectType.controllableObject) {
      const props: IOProps = {
        id: validationResult.id,
        states: states ? states : [],
        name: validationResult.name,
        alt: validationResult.alt,
        position: validationResult.position,
        image: image,
        modal: modal ? modal : "",
        onDropItems: onDropItems ? onDropItems : [],
        onclick: onclick ? onclick : [],
        defaultState: defaultState ? defaultState : "",
        defaultUserInput: defaultUserInput ? defaultUserInput : "",
        userScale,
        sceneScale,
        objectScale,
        text: text,
        label,
        modals,
        buttons,
        moveMenu,
      };

      return <ControllableObject ref={(ref) => (obj = ref)} {...props} />;
    } else if (
      validationResult.type == objectType.compositeInteractableObject
    ) {
      const props: CompositeIOProps = {
        id: validationResult.id,
        states: states ? states : [],
        name: validationResult.name,
        alt: validationResult.alt,
        position: validationResult.position,
        image: image,
        modal: modal ? modal : "",
        onDropItems: onDropItems ? onDropItems : [],
        onclick: onclick ? onclick : [],
        defaultState: defaultState ? defaultState : "",
        defaultUserInput: defaultUserInput ? defaultUserInput : "",
        userScale,
        sceneScale,
        objectScale,
        text: text,
        label,
        modals,
        buttons,
        objects: objects ? objects : [],
      };
      return (
        <CompositeInteractableObject ref={(ref) => (obj = ref)} {...props} />
      );
    } else if (
      validationResult.type == objectType.customObject &&
      customComponentID
    ) {
      const props: CustomObjectProps = {
        id: validationResult.id,
        customComponentID: customComponentID,
        name: name,
        position: position,
        moveMenu: moveMenu,
      };
      return <CustomObject ref={(ref) => (obj = ref)} {...props} />;
    }

    return <></>;
  }
}

export default IOFactory;
